import { LOCAL_LANGUAGE_KEY } from 'constants/localStorageKey';
import React, { lazy, Suspense, useEffect, useMemo } from 'react';
import enUS from 'antd/lib/locale/en_US';
import { Switch, useLocation, Route, useHistory, Redirect } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { I18nWrapper, I18nContextProvider, ErrorBoundary, setLocale } from '@miotech/shared';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';
import dayjs from 'dayjs';
import FillSpin from 'components/fill-spin';
import useRedux from 'hooks/useRedux';
import qs from 'query-string';
import { ThemeProvider } from '@miotech/prism';
import THEME from 'styles/colors';
import { antdPrefixCls } from 'styles';
import FallbackError from 'components/fallback-error';
import FONT from 'styles/fonts';
import { EUserLanguage } from '@miotech/esghub-web-lib-common';
import { getLanguageByNavigatorLanguage } from 'utils';
import Turnstile from 'components/turnstile';
import { useBoolean } from 'ahooks';
import { styleSpin } from './index.style';
import AdminContainer from './admin-container';
import PtiLayout from './pti-layout';

const LoginPage = lazy(() => import('pages/login'));
const ProjectProfileFormPage = lazy(() => import('pages/project-profile/form'));

dayjs.extend(tz);
dayjs.extend(utc);
Spin.setDefaultIndicator(<LoadingOutlined style={{ fontSize: 24, color: THEME.branding }} spin />);

const App: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const languageCache = EUserLanguage.EN_US;
  const { dispatch } = useRedux(s => ({
    userInfo: s.mainApp.userInfo,
  }));

  const antdLanguage = useMemo(() => {
    switch (languageCache) {
      case EUserLanguage.EN_US:
        return enUS;
      default:
        return enUS;
    }
  }, [languageCache]);

  useEffect(() => {
    const urlLanguage = qs.parseUrl(history.location.search).query?.['language'] as string;
    const localLanguage = localStorage.getItem(LOCAL_LANGUAGE_KEY);
    const browserLanguage =
      navigator.language ||
      ((navigator as unknown) as {
        browserLanguage: string;
      }).browserLanguage;
    const language = (urlLanguage || localLanguage || getLanguageByNavigatorLanguage(browserLanguage)) as EUserLanguage;
    const defaultLanguage = Object.values(EUserLanguage).includes(language) ? language : EUserLanguage.EN_US;
    dispatch.mainApp.updateLanguageCache(defaultLanguage);
    localStorage.setItem(LOCAL_LANGUAGE_KEY, defaultLanguage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 更新 shared 里的语言，保证接口调用正确
  useEffect(() => {
    // shared 里的类型无法引用，用any代替
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setLocale(languageCache as any);
  }, [languageCache]);

  useEffect(() => {
    // 需要配置 dayjs 使用正确的语言包 否则 DatePicker组件多语言会有问题
    dayjs.locale(antdLanguage.locale);
  }, [antdLanguage]);

  const [isHuman, { setTrue }] = useBoolean(false);

  if (!languageCache) return <FillSpin spinning className={styleSpin} />;

  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <I18nWrapper staticResource={{}} language={languageCache as any}>
      <I18nContextProvider language={languageCache} resourcePath="/locales">
        <ConfigProvider
          locale={antdLanguage}
          prefixCls={antdPrefixCls}
          theme={{
            token: {
              colorPrimary: THEME.branding,
              colorPrimaryHover: THEME.branding,
              colorError: THEME.warning,
              colorSplit: THEME.fourth,
              colorBorderSecondary: THEME.fourth,
              controlItemBgActive: THEME.branding_10,
              controlItemBgActiveHover: THEME.branding_10,
              borderRadius: 2,
              borderRadiusXS: 2,
              borderRadiusLG: 2,
              borderRadiusSM: 2,
              fontFamily: FONT.roboto,
            },
            components: {
              Slider: {
                handleSize: 18,
                handleSizeHover: 20,
              },
              Tooltip: {
                colorBgSpotlight: THEME.background,
                colorTextLightSolid: THEME.primary,
              },
              Input: {
                colorBorder: THEME.fourth,
                colorBgContainerDisabled: THEME.base,
                colorTextDisabled: THEME.secondary,
                colorTextPlaceholder: THEME.fourth,
              },
              Tabs: {
                colorTextDisabled: THEME.third,
              },
              Select: {
                colorBorder: THEME.fourth,
                colorErrorHover: THEME.warning,
                colorTextPlaceholder: THEME.fourth,
                controlHeight: 28,
                optionActiveBg: THEME.base,
                optionSelectedBg: THEME.branding_10,
              },
              Dropdown: {
                paddingXXS: 0,
                controlItemBgHover: THEME.base,
              },
              Menu: {
                itemActiveBg: THEME.branding_10,
                itemHoverBg: THEME.secondary,
              },
              Table: {
                headerBg: 'transparent',
                headerColor: THEME.secondary,
                headerSplitColor: THEME.background,
                rowHoverBg: THEME.base,
                cellPaddingBlockSM: 5,
                rowSelectedBg: THEME.background,
                rowSelectedHoverBg: THEME.base,
                colorBorderSecondary: THEME.fifth,
              },
              Button: {
                defaultBg: THEME.base,
                colorLink: THEME.branding,
                colorLinkHover: THEME.branding,
                // dangerColor: rgba(THEME.warning, 0.5),
              },
              Modal: {},
              Upload: {
                borderRadiusLG: 4,
              },
              Pagination: {
                itemBg: 'transparent',
              },
            },
          }}
        >
          <ThemeProvider
            value={{
              system: 'light',
              branding: 'brandingEsghub',
            }}
          >
            <PtiLayout>
              <Suspense fallback={<FillSpin spinning className={styleSpin} />}>
                <ErrorBoundary fallback={<FallbackError />}>
                  {isHuman ? (
                    <Switch location={location}>
                      {/* 未登录页面 */}
                      <Route path="/" exact component={ProjectProfileFormPage} />
                      <Route path="/login" exact component={LoginPage} />

                      {/* 已登录 */}
                      <Route path="/admin" component={AdminContainer} />

                      {/* 其他未命中 */}
                      <Redirect to="/" />
                    </Switch>
                  ) : (
                    <Turnstile onSuccess={setTrue} />
                  )}
                </ErrorBoundary>
              </Suspense>
            </PtiLayout>
          </ThemeProvider>
        </ConfigProvider>
      </I18nContextProvider>
    </I18nWrapper>
  );
};

export default App;
